/**
 * <EmptyState> — canonical empty state.
 *
 * Replaces the ~30 ad-hoc empty-state implementations from Phase-1 §5.7
 * (Strategist B) with a single primitive that locks the structural
 * pattern from the master prompt §2.13:
 *
 *   - Title (4-7 words, sentence case)
 *   - Supporting line (one sentence)
 *   - Primary action (optional)
 *
 * Variants codify the copy taxonomy:
 *
 *   - 'no-data'           — feature works, no records yet
 *   - 'no-results'        — search/filter returned nothing
 *   - 'permission-denied' — user lacks permission. Visually distinct
 *                           from no-data so the UI never reveals
 *                           "this exists but you can't see it" (M18).
 *   - 'error'             — fetch / load error
 *   - 'feature-locked'    — feature flag / subscription gate
 *
 * The copy itself comes from the call site — this primitive provides
 * the structure. <Heading> level 3 for title, muted body-sm for
 * description.
 *
 * Spec: docs/design-system/08-modals-drawers-feedback.md, 10-microcopy-voice.md.
 */

import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Heading } from '../../primitives/Heading';
import { Text } from '../../primitives/Text';

export type EmptyStateVariant =
  | 'no-data'
  | 'no-results'
  | 'permission-denied'
  | 'error'
  | 'feature-locked';

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Variant — drives default icon tone and structure cues. Default 'no-data'. */
  variant?: EmptyStateVariant;
  /** Title — 4-7 words, sentence case. */
  title: ReactNode;
  /** Supporting line — one sentence, ends with a period. */
  description?: ReactNode;
  /** Optional Lucide icon. Pass any ReactNode (e.g. `<InboxIcon />`). */
  icon?: ReactNode;
  /** Primary action — typically a <Button>. */
  action?: ReactNode;
  /** Secondary action — typically a <Button variant='ghost'>. */
  secondaryAction?: ReactNode;
  /** Render compact — for table empty cells, drawer panels. */
  compact?: boolean;
}

const ICON_TONE_CLASSES: Record<EmptyStateVariant, string> = {
  'no-data': 'text-muted',
  'no-results': 'text-muted',
  'permission-denied': 'text-warning-muted',
  error: 'text-danger',
  'feature-locked': 'text-info-muted',
};

export function EmptyState({
  variant = 'no-data',
  title,
  description,
  icon,
  action,
  secondaryAction,
  compact = false,
  className,
  ...rest
}: EmptyStateProps) {
  return (
    <div
      role={variant === 'error' ? 'alert' : undefined}
      className={cn(
        'flex flex-col items-center text-center',
        compact ? 'py-6 gap-2' : 'py-12 gap-3',
        className,
      )}
      {...rest}
    >
      {icon && (
        <div
          aria-hidden="true"
          className={cn(
            ICON_TONE_CLASSES[variant],
            compact ? 'text-2xl' : 'text-3xl',
            'flex items-center justify-center',
          )}
        >
          {icon}
        </div>
      )}
      <div className={cn('flex flex-col items-center', compact ? 'gap-0.5' : 'gap-1')}>
        <Heading level={3}>{title}</Heading>
        {description && (
          <Text variant="body-sm" tone="muted" className="max-w-md">
            {description}
          </Text>
        )}
      </div>
      {(action || secondaryAction) && (
        <div className={cn('flex items-center gap-2', compact ? 'mt-1' : 'mt-2')}>
          {secondaryAction}
          {action}
        </div>
      )}
    </div>
  );
}

EmptyState.displayName = 'EmptyState';

export default EmptyState;
